// Lokala fotografsidor per ort. Bygger head + komponent från `fotoCities`
// så att varje ortssida får unik metadata, LocalBusiness-schema och lokal copy.

import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { LocalMobileGallery } from "@/components/site/landing/LocalMobileGallery";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import {
  FotoPriceList,
  FotoServiceSpec,
  FotoTermsSection,
} from "@/components/site/foto/FotoCommerce";
import { coverPhotoFor, galleryPhotos } from "@/content/photo-gallery";
import { fotoCities, PHOTOGRAPHER } from "@/content/foto";
import { fotoCityCopyFor } from "@/content/foto-city-copy";
import { fotoServiceCitiesFor } from "@/content/foto-service-cities";
import {
  aeoGeo,
  breadcrumbs,
  faqPage,
  imageGallery,
  person,
  photoBusiness,
  serviceSchema,
  speakable,
} from "@/lib/seo/schema";


export function fotoCityRoute(slug: string) {
  const city = fotoCities.find((c) => c.slug === slug);
  if (!city) throw new Error(`Unknown foto city ${slug}`);
  const PATH = city.path;
  const cover = coverPhotoFor(city.slug);
  const copy = fotoCityCopyFor(city.slug);
  const localPages = fotoServiceCitiesFor(city.slug);
  const TITLE = `Fotograf ${city.city} — företagsfotograf | Fenny`;
  const DESCRIPTION = `Fotograf ${city.city} för företag: porträtt, kampanjbilder, produktfoto och event. Studio eller på plats hos er. Offert per uppdrag, svar inom en arbetsdag.`;
  // Körsida för Google Business Profile-bokning (finns i dag för Stockholm).
  const BOOKING_PATH = city.slug === "stockholm" ? "/foto/boka-fotograf-stockholm" : null;

  const FAQ: LandingFAQ[] = [
    {
      q: `Vad kostar en fotograf i ${city.city}?`,
      a: `Vi arbetar med offert per uppdrag i stället för fast prislista. Priset styrs av tid på plats, antal färdiga bilder, ljussättning, retuschnivå och användningsrätt. Ni får ett fast pris innan dagen bokas.`,
    },
    {
      q: `Fotograferar ni i våra lokaler i ${city.city}?`,
      a: `Ja. Vi tar med mobil studio och bygger upp fotoplatsen hos er — det behövs ungefär tre gånger tre meter och 45 minuters riggning. Vi fotograferar också i studio när ni vill ha ren bakgrund.`,
    },
    {
      q: `Vilka områden täcker ni utanför ${city.city}?`,
      a: `Vi arbetar i hela ${city.region} och reser i övriga Sverige. Typiska områden: ${city.areas.join(", ")}.`,
    },
    {
      q: `Hur snabbt får vi bilderna?`,
      a: `Retuscherade bilder levereras normalt inom 3–5 arbetsdagar. Vid event kan vi leverera ett första urval löpande under dagen.`,
    },
    {
      q: `Kan ni filma samtidigt?`,
      a: `Ja. Fenny är i grunden ett filmproduktionsbolag, så vi kan bemanna med både fotograf och filmteam samma dag — vilket sänker totalkostnaden jämfört med två separata produktioner.`,
    },
    ...(copy?.faq ?? []),
  ];

  return {
    head: () => ({
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: `Fotograf ${city.city} — Fenny Foto` },
        {
          property: "og:description",
          content: `Företagsporträtt, kampanjbilder och event i ${city.city}. Offert per uppdrag.`,
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `https://fenny.se${PATH}` },
        { name: "geo.region", content: "SE" },
        { name: "geo.placename", content: city.city },
        { name: "twitter:card", content: "summary_large_image" },
        ...(cover.src.startsWith("/")
          ? [
              { property: "og:image", content: `https://fenny.se${cover.src}` },
              { name: "twitter:image", content: `https://fenny.se${cover.src}` },
            ]
          : []),

      ],
      links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            aeoGeo({
              path: PATH,
              name: TITLE,
              description: DESCRIPTION,
              question: copy?.question ?? `Vilken fotograf kan företag i ${city.city} anlita?`,
              answer: copy?.answer ?? `Fenny Foto arbetar som fotograf i ${city.city} och ${city.region} med företagsporträtt, kampanjbilder, produktfoto och eventfoto — i studio eller med mobil studio hos er. Priset offereras per uppdrag utifrån tid, antal bilder, retusch och användningsrätt, och retuscherade bilder levereras inom 3–5 arbetsdagar. Kontaktperson är ${PHOTOGRAPHER.name}, hey@fenny.se.`,
              speakableSelectors: ["[data-quick-answer]"],
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            photoBusiness({
              city: city.city,
              path: PATH,
              description: DESCRIPTION,
              region: city.region,
              images: galleryPhotos.slice(0, 12),
              email: "hey@fenny.se",
              contactName: PHOTOGRAPHER.name,
              ...(BOOKING_PATH ? { bookingPath: BOOKING_PATH } : {}),

            }),
          ),
        },

        {
          type: "application/ld+json",
          children: JSON.stringify(
            serviceSchema({
              name: `Fotograf ${city.city}`,
              path: PATH,
              serviceType: "Företagsfotografering",
              description: DESCRIPTION,
              areaServed: [city.city, city.region, ...city.areas],
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            imageGallery({
              name: `Fenny Foto — ${city.city}`,
              path: PATH,
              description: `Urval ur Fennys fotoportfolio, tillgänglig för uppdrag i ${city.city}.`,
              images: galleryPhotos.slice(0, 12),
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            person({
              name: PHOTOGRAPHER.name,
              jobTitle: PHOTOGRAPHER.role,
              email: "hey@fenny.se",
              path: PATH,
              description: PHOTOGRAPHER.bio,
              knowsAbout: [
                `Fotograf ${city.city}`,
                `Företagsfotograf ${city.city}`,
                "Företagsporträtt",
                "Kampanjfoto",
                "Produktfoto",
                "Eventfoto",
              ],
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: "Hem", path: "/" },
              { name: "Foto", path: "/foto" },
              { name: `Fotograf ${city.city}`, path: PATH },
            ]),
          ),
        },
        ...(BOOKING_PATH
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "ReserveAction",
                  name: `Boka fotograf i ${city.city}`,
                  target: {
                    "@type": "EntryPoint",
                    urlTemplate: `https://fenny.se${BOOKING_PATH}`,
                    inLanguage: "sv-SE",
                    actionPlatform: [
                      "https://schema.org/DesktopWebPlatform",
                      "https://schema.org/MobileWebPlatform",
                    ],
                  },
                  result: { "@type": "Reservation", name: `Fotouppdrag i ${city.city}` },
                  agent: { "@type": "LocalBusiness", name: "Fenny", url: `https://fenny.se${PATH}` },
                }),
              },
            ]
          : []),
      ],
    }),
    component: function FotoCityPage() {
      return (
        <LandingPage
          heroImage={{ src: cover.src, alt: `Fotograf i ${city.city} — ${cover.alt}` }}
          eyebrow={`Fotograf ${city.city}`}
          title={<>Fotograf {city.city} — företagsfotograf för bolag som vill synas ordentligt.</>}
          lede={city.intro}
          tldr={
            <p>
              Fenny Foto fotograferar <strong>företagsporträtt</strong>,{" "}
              <strong>kampanjbilder</strong>, <strong>produktfoto</strong> och{" "}
              <strong>event</strong> i {city.city} och {city.region}. Leverans inom{" "}
              <strong>3–5 arbetsdagar</strong>, fri användningsrätt i egna kanaler
              och <strong>offert per uppdrag</strong>.
            </p>
          }
          ctaPrimary={
            BOOKING_PATH
              ? { label: "Boka fotograf", to: BOOKING_PATH }
              : { label: "Begär offert", to: "#offert" }
          }
          ctaSecondary={{ label: "Se portfolion", to: "/foto" }}
          trackingId={`foto_${city.slug}`}
          stills={false}
          videos={false}
          mobileCollapseAfter={2}
          afterHero={
            <>
              <LocalMobileGallery
                photos={galleryPhotos.slice(0, 6)}
                label={`Foto · ${city.city}`}
                ctaTo={BOOKING_PATH ?? "#offert"}
                ctaLabel={BOOKING_PATH ? `Boka fotograf i ${city.city}` : "Begär offert"}
              />
              <div className="hidden py-12 md:block">
                <PhotoMasonry
                  photos={galleryPhotos.slice(0, 12)}
                  filterable={false}
                  initial={9}
                  eyebrow="Portfolio"
                  heading="Ett urval ur portfolion"
                />
              </div>
            </>
          }
          bullets={[
            {
              title: "Studio eller på plats",
              body: `Vi fotograferar i studio eller bygger mobil studio i era lokaler i ${city.city}.`,
            },
            {
              title: "Enhetligt manér",
              body: "Ljus och beskärning dokumenteras så att kompletteringar matchar originalserien.",
            },
            {
              title: "Fast pris i förväg",
              body: "Offert med specificerat innehåll — inga öppna timpriser.",
            },
          ]}
          sections={[
            ...(BOOKING_PATH
              ? [
                  {
                    id: "boka",
                    heading: `Boka fotograf i ${city.city} direkt`,
                    body: (
                      <p>
                        Vill ni hoppa över researchen finns en kort bokningssida där ni
                        beskriver uppdrag, datum och plats — {PHOTOGRAPHER.name} svarar med
                        upplägg och fast pris inom en arbetsdag:{" "}
                        <a
                          className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
                          href={BOOKING_PATH}
                        >
                          boka fotograf i {city.city}
                        </a>
                        . Samma länk används i vår Google-företagsprofil.
                      </p>
                    ),
                  },
                ]
              : []),
            ...(copy
              ? [
                  {
                    id: "marknad",
                    heading: `Fotografering i ${city.city} — så ser behovet ut här`,
                    body: <p>{copy.market}</p>,
                  },
                ]
              : []),
            ...(copy?.sections ?? []).map((s) => ({
              id: s.id,
              heading: s.heading,
              body: <p>{s.body}</p>,
            })),
            {
              id: "omraden",
              heading: `Områden vi fotograferar i kring ${city.city}`,
              body: (
                <p>
                  Vi arbetar i hela {city.region} och åker regelbundet till{" "}
                  {city.areas.join(", ")}. Resa inom orten ingår normalt i offerten;
                  längre resor specificeras separat.
                </p>
              ),
            },
            {
              id: "tjanster",
              heading: `Vad vi fotograferar i ${city.city}`,
              body: (
                <p>
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/foretagsportratt">Företagsporträtt</a>{" "}
                  och headshots,{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/kampanjfoto">kampanjfoto</a>,{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/produktfoto">produktfoto</a>,{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/studiofotografering">studiofotografering</a>{" "}
                  och{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/eventfoto">eventfoto</a>.
                  Behöver ni rörligt också gör vi{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/foto-och-film">foto och film samma dag</a>.
                </p>
              ),
            },
            ...(localPages.length
              ? [
                  {
                    id: "fordjupning",
                    heading: `Fördjupning för ${city.city}`,
                    body: (
                      <p>
                        Varje tjänst har en egen sida med upplägg, tidsåtgång och
                        prisbild för {city.city}:{" "}
                        {localPages.map((p, i) => (
                          <span key={p.slug}>
                            {i > 0 ? " · " : ""}
                            <a
                              className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
                              href={p.path}
                            >
                              {p.nav}
                            </a>
                          </span>
                        ))}
                        .
                      </p>
                    ),
                  },
                ]
              : []),
          ]}
          afterSections={
            <>
              <FotoServiceSpec city={city.city} id={`sa-gar-det-till-${city.slug}`} />
              <FotoPriceList city={city.city} id={`prislista-${city.slug}`} />
              <FotoTermsSection id={`kundvillkor-${city.slug}`} />
            </>
          }
          faq={FAQ}
          riskReducers
          closing={{
            title: `Ska vi fotografera i ${city.city}?`,
            body: "Beskriv uppdraget så återkommer Fenny med upplägg och fast pris inom en arbetsdag.",
            primary: { label: "Begär offert", to: "#offert" },
          }}
          contactSlot={<FotoQuoteForm location={`foto_${city.slug}`} defaultCity={city.city} />}
        />
      );
    },
  };
}
